import { AppUrls } from "../../ts/app";
import { AutoCompleteSearch } from "../../ts/web-components/auto-complete-search";
import { AutocompleteSearchModelType } from "../../ts/web-components/auto-complete-search-model-type";
import { Controller } from "../../ts/controller";
import { Composer } from "../../ts/models/composer";
import { Game } from "../../ts/models/game";
import { Instrument } from "../../ts/models/instrument";
import { Style } from "../../ts/models/style";
import { TabSubmissionPutPost } from "../../ts/models/tab-submission-put-post";
import { Logger } from "../../ts/utils/logger";

class Errors {
    game: string | null = null;
    songName: string | null = null;
    mediaLink: string | null = null;
    tabText: string | null = null;
    instruments: string | null = null;
    styles: string | null = null;
    composers: string | null = null;
    unknown: string | null = null;

    /** Check if any error exists. */
    hasErrors(): boolean {
        return Object.values(this).some((value) => value !== null);
    }

    /** Clear all errors. */
    clear(): void {
        this.game = null;
        this.songName = null;
        this.mediaLink = null;
        this.tabText = null;
        this.instruments = null;
        this.styles = null;
        this.composers = null;
        this.unknown = null;
    }
}

interface TabSubmissionFormInitializeProps {
    initialSubmissionData: TabSubmissionPutPost | null;
    prepopulateStyles: Style[];
    prepopulateInstruments: Instrument[];
    prepopulateComposers: Composer[];
    chosenGame: Game | null;
    chosenInstruments: Instrument[];
    chosenStyles: Style[];
    chosenComposers: Composer[];
    existsInDB: boolean;
    createDraftUrl: string;
    updateDraftUrl: string;
    submitForReviewUrl: string;
}

export class TabSubmissionFormController extends Controller {
    changesExist: boolean = false;
    existsInDB: boolean = false;

    errors: Errors = new Errors();

    createDraftUrl!: string;
    updateDraftUrl!: string;
    submitForReviewUrl!: string;

    saving: boolean = false;

    constructor() {
        super({
            logLabel: 'TabSubmissionFormController',
        });
    }

    public initialize({
        initialSubmissionData,
        prepopulateStyles,
        prepopulateInstruments,
        prepopulateComposers,
        chosenGame,
        chosenInstruments,
        chosenStyles,
        chosenComposers,
        existsInDB,
        createDraftUrl,
        updateDraftUrl,
        submitForReviewUrl,
    }: TabSubmissionFormInitializeProps): void {
        this.errors.clear();
        this.existsInDB = existsInDB;
        this.createDraftUrl = createDraftUrl;
        this.updateDraftUrl = updateDraftUrl;
        this.submitForReviewUrl = submitForReviewUrl;
        this._initializeGameInfo({ initialSubmissionData, chosenGame });
        this._initializeInstrumentsInfo({ initialSubmissionData, prepopulateInstruments, chosenInstruments });
        this._initializeStylesInfo({ initialSubmissionData, prepopulateStyles, chosenStyles });
        this._initializeComposersInfo({ initialSubmissionData, prepopulateComposers, chosenComposers });
        this._initializeOtherInfo({ initialSubmissionData });
        this._setState();
        this._initializeListeners();
        Logger.info(this.logLabel, "Initialized");
    }

    private _initializeGameInfo({
        initialSubmissionData,
        chosenGame,
    }: {
        initialSubmissionData: TabSubmissionPutPost | null;
        chosenGame: Game | null;
    }): void {
        const searchGameElement = this.getElementByIdOrWarn<AutoCompleteSearch<Game>>("submission-search-game");
        if (searchGameElement) {
            searchGameElement.initialize({
                modelType: AutocompleteSearchModelType.game,
                onChange: this._advancedFiltersChanged.bind(this),
                initialSelectedItems: chosenGame ? [chosenGame] : [],
            });
        }
        if (initialSubmissionData?.requested_game) {
            const requestedGameField = this.getElementByIdOrWarn<HTMLInputElement>("requested-game-field");
            if (requestedGameField) {
                requestedGameField.value = initialSubmissionData.requested_game.trim();
            }
        }
    }

    private _initializeInstrumentsInfo({
        initialSubmissionData,
        prepopulateInstruments,
        chosenInstruments,
    }: {
        initialSubmissionData: TabSubmissionPutPost | null;
        prepopulateInstruments: Instrument[];
        chosenInstruments: Instrument[];
    }): void {
        const searchInstrumentsElement = this.getElementByIdOrWarn<AutoCompleteSearch<Instrument>>(
            "submission-search-instruments");
        if (searchInstrumentsElement) {
            searchInstrumentsElement.initialize({
                modelType: AutocompleteSearchModelType.instrument,
                onChange: this._advancedFiltersChanged.bind(this),
                prepopulatedResults: prepopulateInstruments,
            });
            for (const instrument of chosenInstruments) {
                searchInstrumentsElement.addItem(instrument);
            }
        }
        if (initialSubmissionData !== null) {
            const requestedInstrumentsField = this.getElementByIdOrWarn<HTMLInputElement>(
                "requested-instruments-field");
            if (requestedInstrumentsField) {
                requestedInstrumentsField.value = initialSubmissionData.requested_instruments.join(", ");
            }
        }
    }

    private _initializeStylesInfo({
        initialSubmissionData,
        prepopulateStyles,
        chosenStyles,
    }: {
        initialSubmissionData: TabSubmissionPutPost | null;
        prepopulateStyles: Style[];
        chosenStyles: Style[];
    }): void {
        const searchStylesElement = this.getElementByIdOrWarn<AutoCompleteSearch<Style>>(
            "submission-search-styles");
        if (searchStylesElement) {
            searchStylesElement.initialize({
                modelType: AutocompleteSearchModelType.style,
                onChange: this._advancedFiltersChanged.bind(this),
                prepopulatedResults: prepopulateStyles,
            });
            for (const style of chosenStyles) {
                searchStylesElement.addItem(style);
            }
        }
        if (initialSubmissionData !== null) {
            const requestedStylesField = this.getElementByIdOrWarn<HTMLInputElement>(
                "requested-styles-field");
            if (requestedStylesField) {
                requestedStylesField.value = initialSubmissionData.requested_styles.join(", ");
            }
        }
    }

    private _initializeComposersInfo({
        initialSubmissionData,
        prepopulateComposers,
        chosenComposers,
    }: {
        initialSubmissionData: TabSubmissionPutPost | null;
        prepopulateComposers: Composer[];
        chosenComposers: Composer[];
    }): void {
        const searchComposersElement = this.getElementByIdOrWarn<AutoCompleteSearch<Composer>>(
            "submission-search-composers");
        if (searchComposersElement) {
            searchComposersElement.initialize({
                modelType: AutocompleteSearchModelType.composer,
                onChange: this._advancedFiltersChanged.bind(this),
                prepopulatedResults: prepopulateComposers,
            });
            for (const composer of chosenComposers) {
                searchComposersElement.addItem(composer);
            }
        }
        if (initialSubmissionData !== null) {
            const requestedComposersField = this.getElementByIdOrWarn<HTMLInputElement>(
                "requested-composers-field");
            if (requestedComposersField) {
                requestedComposersField.value = initialSubmissionData.requested_composers.join(", ");
            }
        }
    }

    private _initializeOtherInfo({
        initialSubmissionData,
    }: {
        initialSubmissionData: TabSubmissionPutPost | null,
    }): void {
        if (initialSubmissionData !== null) {
            const songField = this.getElementByIdOrWarn<HTMLInputElement>("song-name-field");
            const mediaField = this.getElementByIdOrWarn<HTMLInputElement>("media-link-field");
            const tabField = this.getElementByIdOrWarn<HTMLTextAreaElement>("tab-text-field");
            if (songField) {
                songField.value = initialSubmissionData.name;
            }
            if (mediaField) {
                mediaField.value = initialSubmissionData.media_link || '';
            }
            if (tabField) {
                tabField.value = initialSubmissionData.text;
            }
        }
    }

    private _initializeListeners(): void {
        const textInputFieldIds = [
            "song-name-field",
            "requested-game-field",
            "requested-instruments-field",
            "requested-styles-field",
            "requested-composers-field",
            "media-link-field",
            "tab-text-field",
        ];

        for (const id of textInputFieldIds) {
            const field = this.getElementByIdOrWarn<HTMLInputElement>(id);
            if (field) {
                field.addEventListener("input", () => {
                    this.changesExist = true;
                    this._setState();
                });
            }
        }

        const safeDraftButton = this.getElementByIdOrWarn<HTMLButtonElement>("save-draft-button");
        if (safeDraftButton) {
            safeDraftButton.addEventListener("click", () => this._saveDraft());
        }

        if (this.existsInDB) {
            const submitForReviewButton = this.getElementByIdOrWarn<HTMLButtonElement>("submit-for-review-button");
            if (submitForReviewButton) {
                submitForReviewButton.addEventListener("click", () => this._submitForReview());
            }
        }
    }

    private _setState(): void {
        this._setControlButtonsState();
        this._setChangesExistState();
        this._setErrorStates();
    }

    private _setChangesExistState(): void {
        const changesExistFlag = this.getElementByIdOrWarn<HTMLElement>('submission-unsaved-changes-exist-flag');
        if (changesExistFlag) {
            changesExistFlag.style.display = this.changesExist ? 'block' : 'none';
        }
    }

    private _setControlButtonsState(): void {
        const saveDraftButton = this.getElementByIdOrWarn<HTMLButtonElement>('save-draft-button');
        if (saveDraftButton) {
            saveDraftButton.disabled = !this.changesExist;
        }
        if (this.existsInDB) {
            const submitForReviewButton = this.getElementByIdOrWarn<HTMLButtonElement>('submit-for-review-button');
            if (submitForReviewButton) {
                submitForReviewButton.disabled = !this.changesExist;
            }
        }
    }

    private _setErrorStates(): void {
        const songName = this.getElementByIdOrWarn<HTMLElement>('submission-song-name-error');
        const game = this.getElementByIdOrWarn<HTMLElement>('submission-game-error');
        const instruments = this.getElementByIdOrWarn<HTMLElement>('submission-instruments-error');
        const styles = this.getElementByIdOrWarn<HTMLElement>('submission-styles-error');
        const composers = this.getElementByIdOrWarn<HTMLElement>('submission-composers-error');
        const mediaLink = this.getElementByIdOrWarn<HTMLElement>('submission-media-link-error');
        const tabText = this.getElementByIdOrWarn<HTMLElement>('submission-tab-text-error');
        const unknown = this.getElementByIdOrWarn<HTMLElement>('submission-unknown-error');
        const errorSubmittingFlag = this.getElementByIdOrWarn<HTMLElement>('submission-error-submitting-flag');

        if (songName) {
            songName.style.display = this.errors.songName ? 'block' : 'none';
            songName.textContent = this.errors.songName ?? '';
        }
        if (game) {
            game.style.display = this.errors.game ? 'block' : 'none';
            game.textContent = this.errors.game ?? '';
        }
        if (instruments) {
            instruments.style.display = this.errors.instruments ? 'block' : 'none';
            instruments.textContent = this.errors.instruments ?? '';
        }
        if (styles) {
            styles.style.display = this.errors.styles ? 'block' : 'none';
            styles.textContent = this.errors.styles ?? '';
        }
        if (composers) {
            composers.style.display = this.errors.composers ? 'block' : 'none';
            composers.textContent = this.errors.composers ?? '';
        }
        if (mediaLink) {
            mediaLink.style.display = this.errors.mediaLink ? 'block' : 'none';
            mediaLink.textContent = this.errors.mediaLink ?? '';
        }
        if (tabText) {
            tabText.style.display = this.errors.tabText ? 'block' : 'none';
            tabText.textContent = this.errors.tabText ?? '';
        }
        if (unknown) {
            unknown.style.display = this.errors.unknown ? 'block' : 'none';
            unknown.textContent = this.errors.unknown ?? '';
        }
        if (errorSubmittingFlag) {
            errorSubmittingFlag.style.display = this.errors.hasErrors() ? 'block' : 'none';
        }
    }

    private _trimAll(): void {
        const textFields = [
            'song-name-field',
            'requested-game-field',
            'requested-instruments-field',
            'requested-styles-field',
            'requested-composers-field',
            'media-link-field',
            'tab-text-field',
        ];
        for (const id of textFields) {
            const field = this.getElementByIdOrWarn<HTMLInputElement>(id);
            if (field) {
                field.value = field.value.trim();
            }
        }
    }

    private _sanitizeAndValidateSongName(): void {
        const songField = this.getElementByIdOrWarn<HTMLInputElement>('song-name-field');
        if (songField) {
            songField.value = songField.value.trim();
            if (songField.value.length === 0) {
                this.errors.songName = 'Required';
            } else if (songField.value.length < 2) {
                this.errors.songName = 'Required: Song name is too short.';
            }
        }
    }

    private _sanitizeAndValidateGame(): void {
        const selectedGameField = this.getElementByIdOrWarn<AutoCompleteSearch<Game>>(
            'submission-search-game');
        const requestedGameField = this.getElementByIdOrWarn<HTMLInputElement>(
            'requested-game-field');
        if (!selectedGameField || !requestedGameField) {
            this.errors.game = 'Something went wrong';
            return;
        }
        const selectedGame = selectedGameField.selectedItems.length > 0 ? selectedGameField.selectedItems[0] : null;
        const requestedGame = requestedGameField.value;
        if (selectedGame && requestedGame) {
            this.errors.game = 'Choose either an existing game or request a new game, not both.';
            return;
        }
        if (!selectedGame && !requestedGame) {
            this.errors.game = 'Required: Either search for a game or provide a suggested new game.';
            return;
        }
        if (requestedGame && requestedGame.length < 2) {
            this.errors.game = 'Required: Suggested game name is too short.';
            return;
        }
    }

    private _sanitizeAndValidateInstruments(): void {
        const selectedInstrumentsField = this.getElementByIdOrWarn<AutoCompleteSearch<Instrument>>(
            'submission-search-instruments');
        const requestedInstrumentsField = this.getElementByIdOrWarn<HTMLInputElement>(
            'requested-instruments-field');

        if (!selectedInstrumentsField || !requestedInstrumentsField) {
            this.errors.instruments = 'Something went wrong';
            return;
        }
        const selectedInstruments = [...selectedInstrumentsField.selectedItems];
        const requestedInstruments = requestedInstrumentsField.value;

        if (selectedInstruments.length === 0 && !requestedInstruments) {
            this.errors.instruments = 'Required: set an instrument';
            return;
        }
    }

    private _sanitizeAndValidateStyles(): void {
        const selectedStylesField = this.getElementByIdOrWarn<AutoCompleteSearch<Style>>(
            'submission-search-styles');
        const requestedStylesField = this.getElementByIdOrWarn<HTMLInputElement>(
            'requested-styles-field');

        if (!selectedStylesField || !requestedStylesField) {
            this.errors.styles = 'Something went wrong';
            return;
        }
        const selectedStyles = [...selectedStylesField.selectedItems];
        const requestedStyles = requestedStylesField.value;

        if (selectedStyles.length === 0 && !requestedStyles) {
            this.errors.styles = 'Required: Set at least one style or request a new one.';
            return;
        }
    }

    private _sanitizeAndValidateComposers(): void {
        const selectedComposersField = this.getElementByIdOrWarn<AutoCompleteSearch<Composer>>(
            'submission-search-composers');
        const requestedComposersField = this.getElementByIdOrWarn<HTMLInputElement>(
            'requested-composers-field');

        if (!selectedComposersField || !requestedComposersField) {
            this.errors.composers = 'Something went wrong';
            return;
        }
        const selectedComposers = [...selectedComposersField.selectedItems];
        const requestedComposers = requestedComposersField.value;

        if (selectedComposers.length === 0 && !requestedComposers) {
            this.errors.composers = 'Required: Set at least one composer or request a new one.';
            return;
        }
    }

    private _sanitizeAndValidateMediaLink(): void {
        const mediaField = this.getElementByIdOrWarn<HTMLInputElement>('media-link-field');
        if (mediaField) {
            mediaField.value = mediaField.value.trim();
        }
    }

    private _sanitizeAndValidateTabText(): void {
        const tabField = this.getElementByIdOrWarn<HTMLInputElement>('tab-text-field');
        if (tabField) {
            if (tabField.value.length < 10) {
                this.errors.tabText = 'Required: Tab text is too short.';
            }
        }
    }

    private _generateDraftSubmissionData(): TabSubmissionPutPost {
        const songNameField = this.getElementByIdOrWarn<HTMLInputElement>('song-name-field');
        const tabTextField = this.getElementByIdOrWarn<HTMLInputElement>('tab-text-field');
        const mediaLinkField = this.getElementByIdOrWarn<HTMLInputElement>('media-link-field');
        const searchGameField = this.getElementByIdOrWarn<AutoCompleteSearch<Game>>('submission-search-game');
        const requestedGameField = this.getElementByIdOrWarn<HTMLInputElement>('requested-game-field');
        const searchInstrumentsField = this.getElementByIdOrWarn<AutoCompleteSearch<Instrument>>('submission-search-instruments');
        const requestedInstrumentsField = this.getElementByIdOrWarn<HTMLInputElement>('requested-instruments-field');
        const searchStylesField = this.getElementByIdOrWarn<AutoCompleteSearch<Style>>('submission-search-styles');
        const requestedStylesField = this.getElementByIdOrWarn<HTMLInputElement>('requested-styles-field');
        const searchComposersField = this.getElementByIdOrWarn<AutoCompleteSearch<Composer>>('submission-search-composers');
        const requestedComposersField = this.getElementByIdOrWarn<HTMLInputElement>('requested-composers-field');

        if (
            !songNameField ||
            !tabTextField ||
            !mediaLinkField ||
            !searchGameField ||
            !requestedGameField ||
            !searchInstrumentsField ||
            !requestedInstrumentsField ||
            !searchStylesField ||
            !requestedStylesField ||
            !searchComposersField ||
            !requestedComposersField
        ) {
            this.errors.unknown = 'Something went wrong.';
            Logger.error(this.logLabel, 'Failed to generate submission data due to missing required fields.');
            throw new Error('Missing required fields for submission.');
        }

        // Sanitize all text fields
        songNameField.value = songNameField.value.trim();
        tabTextField.value = tabTextField.value.trim();
        mediaLinkField.value = mediaLinkField.value.trim();
        requestedGameField.value = requestedGameField.value.trim();
        requestedInstrumentsField.value = requestedInstrumentsField.value.trim();
        requestedStylesField.value = requestedStylesField.value.trim();
        requestedComposersField.value = requestedComposersField.value.trim();

        const submissionData = new TabSubmissionPutPost();
        // Note: 10 === TabSubmissionStatus.DRAFT
        submissionData.status = 10;
        submissionData.name = songNameField.value;
        submissionData.text = tabTextField.value;
        submissionData.media_link = mediaLinkField.value || null;
        submissionData.game = searchGameField.selectedItems[0]?.id || null;
        submissionData.requested_game = requestedGameField.value || null;
        submissionData.instruments = searchInstrumentsField.selectedItems.map((i) => i.id);
        submissionData.composers = searchComposersField.selectedItems.map((c) => c.id);
        submissionData.styles = searchStylesField.selectedItems.map((s) => s.id);
        submissionData.requested_instruments = requestedInstrumentsField.value
            .split(',')
            .map((i) => i.trim())
            .filter((i) => i);
        submissionData.requested_composers = requestedComposersField.value
            .split(',')
            .map((c) => c.trim())
            .filter((c) => c);
        submissionData.requested_styles = requestedStylesField.value
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s);

        return submissionData;
    }

    private async _saveDraft(): Promise<void> {
        this.errors.clear();
        this._trimAll();
        this._sanitizeAndValidateGame();
        this._sanitizeAndValidateSongName();
        if (this.errors.hasErrors()) {
            this._setState();
            return;
        }

        this.saving = true;
        this._setState();
        try {
            const submissionData = this._generateDraftSubmissionData();
            const url = this.existsInDB ? this.updateDraftUrl : this.createDraftUrl;
            const response = await fetch(url, {
                method: this.existsInDB ? 'PATCH' : 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(submissionData),
            });

            if (!response.ok) {
                throw new Error(`Failed to save draft: ${response.statusText}`);
            }

            Logger.info(this.logLabel, 'Draft saved successfully');
            window.location.assign(AppUrls.tabSubmissions);
        } catch (e) {
            this.errors.unknown = 'Failed to save draft. Please try again later.';
            Logger.error(this.logLabel, `Error saving draft: ${e}`);
        } finally {
            this.saving = false;
            this._setState();
        }
    }

    private async _submitForReview(): Promise<void> {
        this.errors.clear();
        this._trimAll();
        this._sanitizeAndValidateGame();
        this._sanitizeAndValidateSongName();
        this._sanitizeAndValidateMediaLink();
        this._sanitizeAndValidateTabText();
        this._sanitizeAndValidateInstruments();
        this._sanitizeAndValidateStyles();
        this._sanitizeAndValidateComposers();

        if (this.errors.hasErrors()) {
            this._setState();
            return;
        }

        this.saving = true;
        this._setState();
        try {
            // Save the draft first if applicable.
            if (this.changesExist) {
                const submissionData = this._generateDraftSubmissionData();
                const draftResponse = await fetch(this.updateDraftUrl, {
                    method: 'PATCH',
                    credentials: 'same-origin',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(submissionData),
                });

                if (!draftResponse.ok) {
                    const errorData = await draftResponse.json();
                    this.errors.unknown = this.parseErrors(errorData).join('\n');
                    throw new Error(`Failed to save before submitting: ${draftResponse.statusText}`);
                }

                Logger.info(this.logLabel, 'Draft saved successfully');
            }

            // Move to submitted status.
            const submitResponse = await fetch(this.submitForReviewUrl, {
                method: 'PATCH',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!submitResponse.ok) {
                const errorData = await submitResponse.json();
                this.errors.unknown = this.parseErrors(errorData).join('\n');
                throw new Error(`Failed to submit for review: ${submitResponse.statusText}`);
            }

            Logger.info(this.logLabel, 'Submitted for review successfully');
            window.location.assign(AppUrls.tabSubmissions);
        } catch (e) {
            if (this.errors.unknown === null) {
                this.errors.unknown = 'Failed to submit for review. Please try again later.';
            }
            Logger.error(this.logLabel, `Error submitting for review: ${e}`);
        } finally {
            this.saving = false;
            this._setState();
        }
    }

    private _advancedFiltersChanged(): void {
        this.changesExist = true;
        this._setState();
    }
}
